"use client";

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export interface DropdownItem {
    value: string;
    label: string;
    href?: string;
}

interface DropdownProps {
    items: DropdownItem[];
    onSelect?: (value: string) => void;
    defaultValue?: string;
    placeholder?: string;
    className?: string;
    skipSelected?: boolean;
}

export const Dropdown: React.FC<DropdownProps> = ({
    items,
    onSelect,
    defaultValue,
    className,
    skipSelected,
    placeholder = "Выберите...",
}) => {
    const defaultItem = items.find((item) => item.value === defaultValue);
    const [isOpen, setIsOpen] = useState(false);
    const [selected, setSelected] = useState<DropdownItem | undefined>(defaultItem);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const toggle = () => setIsOpen((prev) => {
        if (!prev && items?.length <= 1) {
            return;
        }
        return !prev;
    });

    const handleSelect = (item: DropdownItem) => {
        setSelected(item);
        setIsOpen(false);
        if (onSelect) onSelect(item.value);
    };

    useEffect(() => {
        const defaultItem = items.find((item) => item.value === defaultValue);
        setSelected(defaultItem);
    }, [defaultValue, items]);

    // Закрытие при клике вне области компонента
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative inline-block text-center" ref={dropdownRef} style={{ fontFamily: 'Futura, sans-serif' }}>
            <div className="flex flex-col items-center">
                <div
                    onClick={toggle}
                    className="cursor-pointer font-bold text-xl sm:text-2xl inline-flex justify-between items-center uppercase text-sky-950 hover:text-sky-800 transition-all"
                >
                    <span className="max-w-72 truncate">{selected?.label || placeholder}</span>
                    <svg className={`w-8 h-8 translate-y-0.5 text-inherit transition-transform ${isOpen ? "rotate-180" : ""} ${items?.length > 1 ? "opacity-100" : "opacity-50"}`} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m8 10 4 4 4-4" />
                    </svg>
                </div>

                <AnimatePresence>
                    {isOpen && (
                        <motion.ul
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            className="absolute z-10 top-8 w-60 bg-slate-900/85 rounded-2xl shadow-lg overflow-hidden flex flex-col gap-1 p-1"
                        >
                            {items.map((item) => {
                                const isSelected = selected?.value === item.value;
                                if (isSelected) {
                                    if (skipSelected) {
                                        return null;
                                    }
                                }

                                let className = `cursor-pointer rounded-xl font-medium w-full text-xl uppercase text-center px-4 py-2 text-white transition ${isSelected ? "bg-gray-100 hover:bg-gray-200 font-semibold !text-sky-950" : "active:scale-95 hover:bg-gray-100 hover:text-gray-800"}`;

                                return (
                                    <li key={item.value}>
                                        <button
                                            onClick={() => !isSelected ? handleSelect(item) : setIsOpen(false)}
                                            className={className}
                                        >
                                            {item.label}
                                        </button>
                                    </li>
                                )
                            })}
                        </motion.ul>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};
